import React, { useState, useEffect, useCallback } from 'react';
import { auditAPI } from '../../api/api';
import { Plus, X, Clock, CheckCircle2, AlertTriangle, RefreshCw, Search } from 'lucide-react';
import { FOLLOWUP_STATUS_COLORS } from './constants';

const AuditFollowUps = ({ engagement, observations }) => {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    observation_id: '', responsible_department: '', responsible_person: '',
    target_closure_date: '', follow_up_date: '', follow_up_remarks: '',
  });

  const loadFollowUps = useCallback(async () => {
    if (!engagement?.id) return;
    setLoading(true);
    try {
      const res = await auditAPI.getFollowUps({ audit_id: engagement.id });
      setFollowUps(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [engagement?.id]);

  useEffect(() => { loadFollowUps(); }, [loadFollowUps]);

  const handleCreate = async () => {
    try {
      await auditAPI.createFollowUp({ ...form, audit_id: engagement.id });
      setShowForm(false);
      setForm({ observation_id: '', responsible_department: '', responsible_person: '', target_closure_date: '', follow_up_date: '', follow_up_remarks: '' });
      loadFollowUps();
    } catch (e) { console.error(e); alert('Failed to create follow-up'); }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'Closed') {
        updateData.closure_verified_date = new Date().toISOString().split('T')[0];
      }
      await auditAPI.updateFollowUp(id, updateData);
      loadFollowUps();
    } catch (e) { console.error(e); }
  };

  const handleUpdateRemarks = async (id, remarks) => {
    try {
      await auditAPI.updateFollowUp(id, { follow_up_remarks: remarks, follow_up_date: new Date().toISOString().split('T')[0] });
      loadFollowUps();
      setEditingId(null);
    } catch (e) { console.error(e); }
  };

  const getObsTitle = (obsId) => {
    const obs = observations.find(o => o.id === obsId);
    return obs?.generated?.title || obs?.hint?.substring(0, 50) || 'Unknown';
  };

  const isOverdue = (targetDate) => {
    if (!targetDate) return false;
    return targetDate < new Date().toISOString().split('T')[0];
  };

  return (
    <div className="space-y-4" data-testid="audit-followups-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-800">Follow-Up Tracker</h3>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700"
          data-testid="add-followup-btn"
        >
          <Plus className="w-4 h-4" /> New Follow-Up
        </button>
      </div>

      {/* Stats Row */}
      {followUps.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {['Open', 'In Progress', 'Awaiting Evidence', 'Closed'].map(status => {
            const count = followUps.filter(f => f.status === status).length;
            return (
              <div key={status} className={`rounded-lg p-3 text-center ${FOLLOWUP_STATUS_COLORS[status] || 'bg-gray-50'}`}>
                <p className="text-lg font-bold">{count}</p>
                <p className="text-xs font-medium">{status}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Form */}
      {showForm && (
        <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="followup-form">
          <div className="flex justify-between">
            <h4 className="font-medium">New Follow-Up</h4>
            <button onClick={() => setShowForm(false)} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Linked Observation *</label>
            <select value={form.observation_id} onChange={e => setForm({ ...form, observation_id: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="followup-obs-select">
              <option value="">Select Observation</option>
              {observations.map(o => (
                <option key={o.id} value={o.id}>{o.generated?.title || o.hint?.substring(0, 60) || o.id}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Responsible Department</label>
              <input type="text" value={form.responsible_department} onChange={e => setForm({ ...form, responsible_department: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="followup-dept" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Responsible Person</label>
              <input type="text" value={form.responsible_person} onChange={e => setForm({ ...form, responsible_person: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="followup-person" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Closure Date *</label>
              <input type="date" value={form.target_closure_date} onChange={e => setForm({ ...form, target_closure_date: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="followup-target-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Follow-Up Date</label>
              <input type="date" value={form.follow_up_date} onChange={e => setForm({ ...form, follow_up_date: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea value={form.follow_up_remarks} onChange={e => setForm({ ...form, follow_up_remarks: e.target.value })} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="followup-remarks" />
          </div>
          <button
            onClick={handleCreate}
            disabled={!form.observation_id || !form.target_closure_date}
            className="w-full py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            data-testid="save-followup-btn"
          >
            Create Follow-Up
          </button>
        </div>
      )}

      {/* Follow-Ups List */}
      {followUps.length === 0 && !showForm && (
        <div className="text-center py-12 text-gray-400">
          <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No follow-ups yet</p>
          <p className="text-sm">Track unresolved observations with follow-up entries</p>
        </div>
      )}

      <div className="space-y-2">
        {followUps.map(fu => (
          <FollowUpCard
            key={fu.id}
            fu={fu}
            getObsTitle={getObsTitle}
            isOverdue={isOverdue}
            onUpdateStatus={handleUpdateStatus}
            onUpdateRemarks={handleUpdateRemarks}
            editingId={editingId}
            setEditingId={setEditingId}
          />
        ))}
      </div>
    </div>
  );
};

const FollowUpCard = ({ fu, getObsTitle, isOverdue, onUpdateStatus, onUpdateRemarks, editingId, setEditingId }) => {
  const [remarks, setRemarks] = useState(fu.follow_up_remarks || '');
  const overdue = isOverdue(fu.target_closure_date) && fu.status !== 'Closed';

  return (
    <div className={`bg-white rounded-xl border p-4 ${overdue ? 'border-red-300 bg-red-50/30' : ''}`} data-testid={`followup-card-${fu.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FOLLOWUP_STATUS_COLORS[fu.status] || 'bg-gray-100'}`}>
              {fu.status}
            </span>
            {overdue && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Overdue
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-800 truncate" title={getObsTitle(fu.observation_id)}>
            {getObsTitle(fu.observation_id)}
          </p>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
            {fu.responsible_department && <span>Dept: {fu.responsible_department}</span>}
            {fu.responsible_person && <span>Person: {fu.responsible_person}</span>}
            <span className={`${overdue ? 'text-red-600 font-medium' : ''}`}>
              Target: {fu.target_closure_date || 'Not set'}
            </span>
          </div>
          {fu.follow_up_remarks && editingId !== fu.id && (
            <p className="text-xs text-gray-500 mt-1 italic">Last remarks: {fu.follow_up_remarks}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <select
            value={fu.status}
            onChange={e => onUpdateStatus(fu.id, e.target.value)}
            className="text-xs border rounded px-1.5 py-1"
            data-testid={`followup-status-${fu.id}`}
          >
            <option value="Open">Open</option>
            <option value="In Progress">In Progress</option>
            <option value="Awaiting Evidence">Awaiting Evidence</option>
            <option value="Closed">Closed</option>
            <option value="Reopened">Reopened</option>
          </select>
          <button
            onClick={() => setEditingId(editingId === fu.id ? null : fu.id)}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
            title="Add follow-up remarks"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {editingId === fu.id && (
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            placeholder="Follow-up remarks..."
            className="flex-1 border rounded-lg px-3 py-1.5 text-sm"
            data-testid={`followup-edit-remarks-${fu.id}`}
          />
          <button
            onClick={() => onUpdateRemarks(fu.id, remarks)}
            className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
            data-testid={`save-followup-remarks-${fu.id}`}
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditFollowUps;
